import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import useEmblaCarousel from 'embla-carousel-react';
import Autoplay from 'embla-carousel-autoplay';
import {
  Scissors, Paintbrush, Eye, Sparkles, Gem, Heart, Activity, Brain,
  Footprints, Apple, Flower2, Dumbbell, PenTool, Smile, GraduationCap,
  ChevronLeft, ChevronRight, Pause, Play, Grid3X3, MessageSquare
} from 'lucide-react';

const specialisms = [
  { label: 'Hair & Barbering', examples: 'Styling · Colour · Barbering', icon: Scissors },
  { label: 'Nails', examples: 'Manicures · Pedicures · Nail art', icon: Paintbrush },
  { label: 'Lashes & Brows', examples: 'Lash extensions · Brow shaping · Threading', icon: Eye },
  { label: 'Beauty', examples: 'Waxing · Makeup · Bridal beauty', icon: Sparkles },
  { label: 'Skin & Aesthetics', examples: 'Facials · Skin treatments · Aesthetics', icon: Gem },
  { label: 'Massage & Bodywork', examples: 'Sports massage · Deep tissue · Relaxation', icon: Heart },
  { label: 'Physical Therapies', examples: 'Physiotherapy · Osteopathy · Chiropractic', icon: Activity },
  { label: 'Talking Therapies', examples: 'Counselling · Psychotherapy · Psychology', icon: Brain },
  { label: 'Foot Health', examples: 'Podiatry · Chiropody · Foot care', icon: Footprints },
  { label: 'Nutrition', examples: 'Dietitians · Nutritionists · Nutrition coaching', icon: Apple },
  { label: 'Complementary Therapies', examples: 'Acupuncture · Reflexology · Reiki', icon: Flower2 },
  { label: 'Movement & Fitness', examples: 'Personal training · Yoga · Pilates', icon: Dumbbell, discuss: true },
  { label: 'Tattoo & Permanent Makeup', examples: 'Tattooing · Microblading · Permanent makeup', icon: PenTool, discuss: true },
  { label: 'Dental Care', examples: 'Dentists · Dental hygienists', icon: Smile, discuss: true },
  { label: 'Training & Academies', examples: 'Beauty training · Therapy training · Practitioner workshops', icon: GraduationCap, discuss: true },
];

function SpecialismCard({ item }) {
  return (
    <div className="bg-[#FAF9F6] rounded-2xl p-5 flex flex-col items-center text-center h-full">
      <div className="w-10 h-10 rounded-xl bg-[#E8EDE5] flex items-center justify-center mb-3">
        <item.icon className="w-5 h-5 text-[#3A4035]" strokeWidth={1.5} />
      </div>
      <p className="text-sm font-medium text-[#1A1A1A] mb-1.5">{item.label}</p>
      <p className="text-[11px] text-[#8A8B85] leading-relaxed">{item.examples}</p>
      {item.discuss && (
        <Link
          to="/book-a-viewing"
          className="mt-2.5 inline-flex items-center gap-1 text-[10px] text-[#3A4035]/70 hover:text-[#3A4035] transition-colors font-medium"
        >
          <MessageSquare className="w-3 h-3" strokeWidth={1.5} />
          Discuss your requirements
        </Link>
      )}
    </div>
  );
}

const CTRL = "w-11 h-11 rounded-full bg-[#3A4035] flex items-center justify-center text-white hover:bg-[#2B3027] focus-visible:ring-2 focus-visible:ring-[#3A4035] focus-visible:ring-offset-2 transition-colors";

export default function SpecialismSection() {
  const [expanded, setExpanded] = useState(false);
  const [isPlaying, setIsPlaying] = useState(true);
  const manualPause = useRef(false);
  const prefersReducedMotion = useRef(false);

  useEffect(() => {
    prefersReducedMotion.current = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReducedMotion.current) setIsPlaying(false);
  }, []);

  const autoplayPlugin = useRef(
    Autoplay({
      delay: 3500,
      stopOnInteraction: false,
      stopOnMouseEnter: true,
      stopOnFocusIn: true,
      playOnInit: !window.matchMedia('(prefers-reduced-motion: reduce)').matches,
    })
  );

  const [emblaRef, emblaApi] = useEmblaCarousel(
    { loop: true, align: 'start', slidesToScroll: 1 },
    [autoplayPlugin.current]
  );

  const scrollPrev = useCallback(() => emblaApi?.scrollPrev(), [emblaApi]);
  const scrollNext = useCallback(() => emblaApi?.scrollNext(), [emblaApi]);

  const togglePlay = useCallback(() => {
    const plugin = autoplayPlugin.current;
    if (!plugin || !emblaApi) return;
    if (isPlaying) {
      plugin.stop();
      manualPause.current = true;
      setIsPlaying(false);
    } else {
      manualPause.current = false;
      plugin.play();
      setIsPlaying(true);
    }
  }, [emblaApi, isPlaying]);

  useEffect(() => {
    if (!emblaApi) return;
    const onStop = () => setIsPlaying(false);
    const onPlay = () => { if (!manualPause.current) setIsPlaying(true); };
    emblaApi.on('autoplay:stop', onStop);
    emblaApi.on('autoplay:play', onPlay);
    return () => {
      emblaApi.off('autoplay:stop', onStop);
      emblaApi.off('autoplay:play', onPlay);
    };
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    const plugin = autoplayPlugin.current;
    if (expanded) {
      plugin?.stop();
    } else if (!manualPause.current && !prefersReducedMotion.current) {
      plugin?.play();
    }
  }, [expanded, emblaApi]);

  return (
    <section className="py-16 sm:py-24 bg-white" data-testid="specialism-section">
      <div className="max-w-6xl mx-auto px-4 sm:px-8">
        {/* Header */}
        <div className="text-center mb-10">
          <h2 className="font-heading text-2xl sm:text-3xl font-medium text-[#1A1A1A] tracking-tight mb-4">
            A space for your business
          </h2>
          <p className="text-base text-[#5C5C55] leading-relaxed max-w-2xl mx-auto">
            Private studios for health, wellbeing and beauty businesses — from independent practitioners to small teams and training academies.
          </p>
        </div>

        {/* Carousel view */}
        {!expanded && (
          <div className="relative" data-testid="specialism-carousel">
            <div ref={emblaRef} className="overflow-hidden rounded-2xl">
              <div className="flex -ml-3">
                {specialisms.map((item) => (
                  <div
                    key={item.label}
                    className="min-w-0 shrink-0 grow-0 basis-[48%] sm:basis-[30%] lg:basis-[18%] pl-3"
                    role="group"
                    aria-roledescription="slide"
                  >
                    <SpecialismCard item={item} />
                  </div>
                ))}
              </div>
            </div>

            {/* Controls */}
            <div className="flex items-center justify-center gap-4 mt-6">
              <button
                onClick={scrollPrev}
                className={CTRL}
                aria-label="Previous category"
                data-testid="carousel-prev"
              >
                <ChevronLeft className="w-5 h-5" strokeWidth={2} />
              </button>
              <button
                onClick={togglePlay}
                className={CTRL}
                aria-label={isPlaying ? 'Pause automatic scrolling' : 'Resume automatic scrolling'}
                data-testid="carousel-pause-play"
              >
                {isPlaying
                  ? <Pause className="w-4 h-4" strokeWidth={2} />
                  : <Play className="w-4 h-4 ml-0.5" strokeWidth={2} />}
              </button>
              <button
                onClick={scrollNext}
                className={CTRL}
                aria-label="Next category"
                data-testid="carousel-next"
              >
                <ChevronRight className="w-5 h-5" strokeWidth={2} />
              </button>
            </div>

            {/* View all */}
            <div className="text-center mt-5">
              <button
                onClick={() => setExpanded(true)}
                className="inline-flex items-center gap-1.5 text-sm text-[#3A4035] font-medium hover:text-[#2B3027] transition-colors"
                data-testid="view-all-specialisms"
              >
                <Grid3X3 className="w-4 h-4" strokeWidth={1.5} />
                View all business types
              </button>
            </div>
          </div>
        )}

        {/* Expanded grid view */}
        {expanded && (
          <div data-testid="specialism-grid">
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 sm:gap-4">
              {specialisms.map((item) => (
                <SpecialismCard key={item.label} item={item} />
              ))}
            </div>
            <div className="text-center mt-6">
              <button
                onClick={() => setExpanded(false)}
                className="inline-flex items-center gap-1.5 text-sm text-[#3A4035] font-medium hover:text-[#2B3027] transition-colors"
                data-testid="collapse-specialisms"
              >
                <ChevronLeft className="w-4 h-4" strokeWidth={1.5} />
                Back to carousel
              </button>
            </div>
          </div>
        )}

        {/* Footer text */}
        <div className="text-center mt-8 space-y-2">
          <p className="text-sm text-[#5C5C55]">
            Don't see your business listed?{' '}
            <Link to="/book-a-viewing" className="text-[#3A4035] font-medium underline underline-offset-2 hover:text-[#2B3027] transition-colors">
              Let's talk
            </Link>{' '}
            about finding the right space for you.
          </p>
          <p className="text-xs text-[#8A8B85]">
            Suitability depends on your business requirements and the facilities available at each location.
          </p>
        </div>
      </div>
    </section>
  );
}
